import { useCallback, useEffect, useState } from 'react'
import {
  createSunoPromptGroup,
  deleteSunoPromptGroup,
  getSunoPromptGroups,
  updateSunoPromptGroup,
  type SunoPromptGroup
} from '../api/suno'
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog'
import { useToast } from '../components/ToastProvider'
import { PromptGroupDialog, PromptGroups, PromptManagementShell } from '../features/suno/PromptManagement'

const emptyPromptGroup = (): SunoPromptGroup => ({
  id: '',
  name: '',
  description: null,
  prompt_count: 0,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString()
})

export const PromptGroupsPage = () => {
  const { toast } = useToast()

  const [sunoPromptGroups, setSunoPromptGroups] = useState<SunoPromptGroup[]>([])
  const [promptGroupsLoading, setPromptGroupsLoading] = useState(false)
  const [promptGroupsError, setPromptGroupsError] = useState<string | null>(null)
  const [promptGroupEditor, setPromptGroupEditor] = useState<SunoPromptGroup | null>(null)
  const [promptGroupSaving, setPromptGroupSaving] = useState(false)
  const [promptGroupToDelete, setPromptGroupToDelete] = useState<SunoPromptGroup | null>(null)
  const [promptGroupDeleting, setPromptGroupDeleting] = useState(false)

  const loadSunoPromptGroups = useCallback(async (search?: string): Promise<void> => {
    setPromptGroupsLoading(true)
    setPromptGroupsError(null)

    try {
      setSunoPromptGroups(await getSunoPromptGroups(search))
    } catch (requestError) {
      setPromptGroupsError(requestError instanceof Error ? requestError.message : 'Unable to load prompt groups.')
    } finally {
      setPromptGroupsLoading(false)
    }
  }, [])

  const openCreatePromptGroupDialog = useCallback((): void => {
    setPromptGroupEditor(emptyPromptGroup())
  }, [])

  useEffect(() => {
    void loadSunoPromptGroups()
  }, [loadSunoPromptGroups])

  const savePromptGroup = async (input: SunoPromptGroup): Promise<void> => {
    const name = input.name.trim()

    if (!name) {
      return
    }

    setPromptGroupSaving(true)
    try {
      const payload = {
        name,
        description: input.description?.trim() || null
      }

      if (input.id) {
        await updateSunoPromptGroup({
          id: input.id,
          ...payload
        })
        toast(`Updated prompt group "${name}".`)
      } else {
        await createSunoPromptGroup(payload)
        toast(`Created prompt group "${name}".`)
      }

      setPromptGroupEditor(null)
      await loadSunoPromptGroups()
    } catch (requestError) {
      toast(requestError instanceof Error ? requestError.message : 'Unable to save prompt group.')
    } finally {
      setPromptGroupSaving(false)
    }
  }

  const confirmDeletePromptGroup = async (): Promise<void> => {
    if (!promptGroupToDelete) {
      return
    }

    setPromptGroupDeleting(true)
    try {
      await deleteSunoPromptGroup(promptGroupToDelete.id)
      toast(`Deleted prompt group "${promptGroupToDelete.name}".`)
      setPromptGroupToDelete(null)
      await loadSunoPromptGroups()
    } catch (requestError) {
      toast(requestError instanceof Error ? requestError.message : 'Unable to delete prompt group.')
    } finally {
      setPromptGroupDeleting(false)
    }
  }

  return (
    <>
      <PromptManagementShell actionLabel="Create group" onAction={openCreatePromptGroupDialog}>
        <PromptGroups
          groups={sunoPromptGroups}
          isLoading={promptGroupsLoading}
          error={promptGroupsError}
          onCreate={openCreatePromptGroupDialog}
          onDelete={(group) => setPromptGroupToDelete(group)}
          onEdit={setPromptGroupEditor}
          onSearch={(search) => void loadSunoPromptGroups(search)}
        />
      </PromptManagementShell>

      {promptGroupEditor && (
        <PromptGroupDialog
          group={promptGroupEditor}
          isSaving={promptGroupSaving}
          onCancel={() => setPromptGroupEditor(null)}
          onChange={setPromptGroupEditor}
          onSave={(input) => void savePromptGroup(input)}
        />
      )}

      {promptGroupToDelete && (
        <DeleteConfirmDialog
          description="This will delete the prompt group and remove its prompt bindings. The prompts themselves stay in the library. This action cannot be undone."
          isDeleting={promptGroupDeleting}
          itemName={promptGroupToDelete.name}
          onCancel={() => setPromptGroupToDelete(null)}
          onConfirm={confirmDeletePromptGroup}
          title="Delete prompt group?"
        />
      )}
    </>
  )
}
